import type { ResultOf } from '@graphql-typed-document-node/core'
import { useAtomValue } from 'jotai'
import { useMemo, useState } from 'react'
import type { PropsWithChildren } from 'react'
import { CourseSelectionInfo } from '~/components/course/CourseSelectionInfo.frag'
import { RestaurantSearchCourse } from '~/components/course/RestaurantSearchCourse.frag'
import { CourseItem } from '~/components/course/sd/CourseComponent'
import { PlanSearchPrice } from '~/components/plan/PlanSearchPrice.frag'
import { PlanSelectionInfo } from '~/components/plan/PlanSelectionInfo.frag'
import { MEAL_PERIOD_LABEL } from '~/lib/constants/constants'
import { getFragmentData } from '~/lib/graphql'
import { tabIcons } from '~/lib/mealPeriod/tab'
import type { MealPeriodTab } from '~/lib/mealPeriod/tab'
import { groupCoursesByMealPeriodTab } from '~/lib/plan/plan'
import { css } from '~/styled-system/css'
import { flex, hstack, vstack } from '~/styled-system/patterns'
import { createTabs } from '~/ui/Tabs'
import { restaurant$ } from '../../scope'
import { courses$ } from './query'

type Course = ResultOf<typeof RestaurantSearchCourse>

export function Courses() {
  const courses = useAtomValue(courses$)
  if (!courses || courses.length === 0) {
    return <CourseContainer />
  }
  return (
    <CourseContainer>
      <CorusesLoaded courses={courses} />
    </CourseContainer>
  )
}

function CourseContainer(props: PropsWithChildren) {
  return (
    <div
      className={vstack({
        bgColor: 'white',
        gap: '5',
        alignItems: 'stretch',
        py: { base: '6', _pc: '8' },
        px: { base: '3', _pc: '8' },
      })}
    >
      <hgroup className={hstack({ justify: 'space-between', alignItems: 'center' })}>
        <h3 className={css({ fontSize: { base: 'xl', _pc: '2xl' }, fontWeight: 'bold' })}>
          WEB予約可能なコース
        </h3>
        <p className={hstack({ gap: '0', color: 'gray.700', fontSize: 'xs' })}>
          お一人様 消費税・サービス料込
        </p>
      </hgroup>
      {props.children}
    </div>
  )
}

function CorusesLoaded(props: { courses: Course[] }) {
  const groupedCourses = useMemo(() => groupCoursesByMealPeriodTab(props.courses), [props.courses])
  const { activeTab, tabs } = useMealPeriodTabs({ tabs: groupedCourses.map(([tab]) => tab) })

  return (
    <div className={flex({ direction: 'column', gap: '3' })}>
      {tabs}
      {groupedCourses.map(([tab, courses]) => {
        if (tab !== activeTab) {
          return null
        }
        return <CourseList key={tab} courses={courses} />
      })}
    </div>
  )
}

const Tabs = createTabs<MealPeriodTab>()

function useMealPeriodTabs(props: { tabs: MealPeriodTab[] }) {
  const [activeTab, setActiveTab] = useState<MealPeriodTab>(props.tabs[0])

  const tabs = (
    <Tabs.Root theme="meal-period" value={activeTab}>
      {props.tabs.map((tab) => (
        <Tabs.Tab key={tab} value={tab} onClick={setActiveTab} Icon={tabIcons[tab]}>
          {MEAL_PERIOD_LABEL[tab]}
        </Tabs.Tab>
      ))}
    </Tabs.Root>
  )

  return { activeTab, tabs }
}

function CourseList({ courses }: { courses: Course[] }) {
  const restaurant = useAtomValue(restaurant$)
  return courses.map((c) => {
    const course = getFragmentData(CourseSelectionInfo, c)
    // XXX: CourseList からコピペ
    // FIXME: searchCourses を inventory と同じ形式にして CourseItem に閉じ込めるべき
    const plans = c.searchPlans.edges
      .map((edge) => {
        const price = getFragmentData(PlanSearchPrice, edge.node.searchPrice!)
        return {
          ...getFragmentData(PlanSelectionInfo, edge.node),
          bestPrice: price.bestPrice,
          priceVaried: price.priceVaried,
          discountRate: price.discountPercent ?? 0,
          pointRate: price.pointRatePercent,
          fixedPoints: price.fixedPoints,
        }
      })
      // 安い順に表示したいのでソートする
      .sort((a, b) => a.bestPrice - b.bestPrice)
    return (
      <CourseItem
        key={course.courseId}
        course={course}
        plans={plans}
        restaurant={restaurant}
        pageType="courseList"
      />
    )
  })
}
